import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/**
 * The package purchase is a PAGE now (Phase 15) — `/packages/:packageId/purchase`.
 *
 * This component is a THIN REDIRECT and nothing else. It is deliberately not a second purchase
 * implementation: the delivery shape of the modal was reimplemented as a page precisely so there is
 * one purchase flow, and two of them would drift — the same failure mode that caused Phases 2 and 3,
 * where two code paths described the same money and disagreed.
 *
 * It keeps the `isOpen` / `onClose` / `package` / `shopId` props so the callers that used to open a
 * dialog (`ItemDetailModal`, `customer/ShopDetails`) need no change at all, and so no caller can
 * accidentally go on rendering a stale copy of the form.
 *
 * A guest reaches the same page: the page is public, exactly as this modal was.
 */
const PackagePurchaseModal = ({ isOpen, onClose, package: pkg, shopId }) => {
  const navigate = useNavigate();

  // The redirect must fire ONCE. `onClose` is passed as an inline arrow by both callers, so it has a
  // new identity on every render — without this guard the effect would re-run and re-navigate on
  // each one.
  const redirected = useRef(false);

  useEffect(() => {
    if (!isOpen || !pkg?._id || redirected.current) {
      return;
    }
    redirected.current = true;

    const query = shopId ? `?shopId=${encodeURIComponent(shopId)}` : "";
    // Close first, so the modal that opened this one is not left mounted underneath.
    if (typeof onClose === "function") {
      onClose();
    }
    navigate(`/packages/${pkg._id}/purchase${query}`);
  }, [isOpen, pkg?._id, shopId, navigate, onClose]);

  return null;
};

export default PackagePurchaseModal;
